import { useEffect } from 'react';
import { useLocation } from '@/compat/react-router-dom';
import { PageMetaData } from '@/components';
import { TopBar, TopNavBar, Footer } from '@/layout';
import Hero from './components/Hero';
import WhoWeServe from './components/WhoWeServe';
import TourOperatorServices from './components/TourOperatorServices';
import MICEServices from './components/MICEServices';
import TransferServices from './components/TransferServices';
import MultilingualExcursions from './components/MultilingualExcursions';
import WhyChooseUs from './components/WhyChooseUs';
import ActionBox from './components/ActionBox';
import { useServicesPage } from '@/hooks/useServicesPage';
import { Spinner } from 'react-bootstrap';
import { scrollToHash } from '@/utils/scrollToHash';

// Component mapping: maps section keys to their React components
const sectionComponentMap = {
  'hero': Hero,
  'who_we_serve': WhoWeServe,
  'tour_operator_services': TourOperatorServices,
  'groups_mice': MICEServices,
  'transfer_services': TransferServices,
  'multilingual_excursions': MultilingualExcursions,
  'how_we_work': WhyChooseUs,
  'action_box': ActionBox,
};

const Services = () => {
  const { sections, loading, error } = useServicesPage();
  const location = useLocation();

  // Handle hash scrolling on page load or when hash changes
  useEffect(() => {
    if (location.hash && !loading) {
      // Wait for all sections to render, then scroll
      const timer = setTimeout(() => {
        scrollToHash(location.hash, 100, 10);
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [location.hash, loading]);

  if (loading) {
    return (
      <>
        <PageMetaData
          title="Professional Travel Services - Tour Operators & MICE"
          description="DMC and inbound tour operator with 40+ years of experience in East Crete. Comprehensive services including hotel contracting, reservations, representative services, transfers, excursions, car rentals, and MICE solutions."
          keywords="DMC Crete, tour operator services, MICE services, hotel contracting, multilingual excursions, transfer services, car rental Crete, group travel, East Crete travel services"
        />
        <TopBar />
        <TopNavBar />
        <main>
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  if (error) {
    return (
      <>
        <PageMetaData
          title="Professional Travel Services - Tour Operators & MICE"
          description="DMC and inbound tour operator with 40+ years of experience in East Crete. Comprehensive services including hotel contracting, reservations, representative services, transfers, excursions, car rentals, and MICE solutions."
          keywords="DMC Crete, tour operator services, MICE services, hotel contracting, multilingual excursions, transfer services, car rental Crete, group travel, East Crete travel services"
        />
        <TopBar />
        <TopNavBar />
        <main>
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
            <p className="text-danger">Error loading services page: {error}</p>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <PageMetaData
        title="Professional Travel Services - Tour Operators & MICE"
        description="DMC and inbound tour operator with 40+ years of experience in East Crete. Comprehensive services including hotel contracting, reservations, representative services, transfers, excursions, car rentals, and MICE solutions."
        keywords="DMC Crete, tour operator services, MICE services, hotel contracting, multilingual excursions, transfer services, car rental Crete, group travel, East Crete travel services"
      />
      <TopBar />
      <TopNavBar />
      <main>
        {sections
          .filter(section => section.is_active)
          .sort((a, b) => {
            // Sort by display_order, then by id as fallback
            const orderA = a.display_order ?? 999;
            const orderB = b.display_order ?? 999;
            if (orderA !== orderB) {
              return orderA - orderB;
            }
            return (a.id ?? 0) - (b.id ?? 0);
          })
          .map((section) => {
            const Component = sectionComponentMap[section.section_key];
            if (!Component) {
              console.warn(`No component mapped for section key: ${section.section_key}`);
              return null;
            }
            return <Component key={section.id || section.section_key} sectionData={section} />;
          })}
      </main>
      <Footer />
    </>
  );
};

export default Services;

